import { fetchStaffUsers, fetchRoles, fetchPermissions, persistRole, persistStaffUser, updateRoleDetails, updateRolePermissions, updateStaffUser, deleteRole, fetchStaffUsersForRole, restoreStaffUserPasswordToDefault, markStaffUserAsApproved, markRoleAsApproved } from '../../services/API';
import router from '../../router';
import { handleNetworkError, showSuccessToast } from '../../utils';

export const getStaffUsers = async ({ commit }) => {
  commit('FETCHING_STAFF_USERS', true);
  commit('SET_STAFF_USERS', []);
  try {
    const staffUsers = await fetchStaffUsers();
    commit('FETCHING_STAFF_USERS', false);
    commit('SET_STAFF_USERS', staffUsers);
  } catch (error) {
    handleNetworkError(error, "Error fetching savings data:");
    commit('FETCHING_STAFF_USERS', false);
    commit('SET_STAFF_USERS', null);
  }
}

export const createStaffUser = async ({ commit, state }, {data, role}) => {
  commit('CREATING_STAFF_USER', true);
  try {
    const staffUser = await persistStaffUser(data);
    commit('CREATING_STAFF_USER', false);

    let staffUsers = state.staffUsers;
    if(!staffUsers)
      staffUsers = [];

    staffUsers.push({...staffUser, userRole: role});

    commit('SET_STAFF_USERS', staffUsers);
    showSuccessToast("Staff user created");
    router.replace('/userAccess/users');
  } catch (error) {
    handleNetworkError(error, "Error creating staff user:");
    commit('CREATING_STAFF_USER', false);
  }
}

export const removeStaffUserFromStore = async ({ commit, state }, staffUserId) => {
  let staffUsers = state.staffUsers;
  if(!staffUsers)
    staffUsers = [];

  staffUsers = staffUsers.filter(user => user._id != staffUserId);
  commit('SET_STAFF_USERS', staffUsers);
}

export const setStaffUserBeingEdited = ({ state, commit }, staffUserId) => {
  if(!staffUserId)
    commit('SET_STAFF_USER_BEING_EDITED', null);

  if(!state.staffUsers)
    return;

  const staffUser = state.staffUsers.find(staffUser => staffUser._id === staffUserId);
  commit('SET_STAFF_USER_BEING_EDITED', staffUser);
}

export const editStaffUser = async ({ commit, state }, {data, role}) => {
  commit('CREATING_STAFF_USER', true);
  try {
    const updatedStaffUser = await updateStaffUser(data);
    commit('CREATING_STAFF_USER', false);

    let staffUsers = state.staffUsers;
    if(!staffUsers)
      staffUsers = [];

    staffUsers = staffUsers.map(staffUser => {
      if(staffUser._id === updatedStaffUser._id){
        return {...updatedStaffUser, userRole: role};
      }
      return staffUser;
    });

    commit('SET_STAFF_USERS', staffUsers);
    showSuccessToast("Staff user updated");
    router.replace('/userAccess/users');
  } catch (error) {
    handleNetworkError(error, "Error updating staff user");
    commit('CREATING_STAFF_USER', false);
  }
}

export const resetStaffUserPassword = async ({ commit, state }, userId) => {
  try {
    let staffUsers = state.staffUsers;
    if(!staffUsers)
      staffUsers = [];

    staffUsers = staffUsers.map(staffUser => {
      if(staffUser._id === userId){
        return {...staffUser, resettingPassword: true};
      }
      return staffUser;
    });
    commit('SET_STAFF_USERS', staffUsers);

    await restoreStaffUserPasswordToDefault(userId);

    staffUsers = staffUsers.map(staffUser => {
      if(staffUser._id === userId)
        return {...staffUser, resettingPassword: false};

      return staffUser;
    });

    commit('SET_STAFF_USERS', staffUsers);
    showSuccessToast("Staff password reset");
  } catch (error) {
    handleNetworkError(error, "Error resetting staff user password:");
    let staffUsers = state.staffUsers;
    if(!staffUsers)
      staffUsers = [];

    staffUsers = staffUsers.map(staffUser => {
      if(staffUser._id === userId){
        return {...staffUser, resettingPassword: false};
      }
      return staffUser;
    });
    commit('SET_STAFF_USERS', staffUsers);
  }
}

export const approveStaffUser = async ({ commit, state }, userId) => {
  let staffUsers = state.staffUsers;
  if(!staffUsers)
    staffUsers = [];

  function setStaffApprovingStatus(approving, approvalStatus){
    staffUsers = staffUsers.map(staffUser => {
      if(staffUser._id === userId){
        let updatedStaffUser = {...staffUser, approving};
        if(approvalStatus !== undefined)
          updatedStaffUser.staffApprovalStatus = approvalStatus;

        return updatedStaffUser;
      }

      return staffUser;
    });
    commit('SET_STAFF_USERS', staffUsers);
  }

  try {
    setStaffApprovingStatus(true);
    await markStaffUserAsApproved(userId);
    setStaffApprovingStatus(false, 1);

    showSuccessToast("Staff user approved");
  } catch (error) {
    handleNetworkError(error, "Error approving staff user:");
    setStaffApprovingStatus(false);
  }
}

export const getRoles = async ({ commit }) => {
  commit('FETCHING_ROLES', true);
  commit('SET_ROLES', []);
  try {
    const roles = await fetchRoles();
    commit('FETCHING_ROLES', false);
    commit('SET_ROLES', roles);
  } catch (error) {
    handleNetworkError(error, "Error fetching savings data:");
    commit('FETCHING_ROLES', false);
    commit('SET_ROLES', null);
  }
}

export const createRole = async ({ commit, state }, {data, newRolePermissions}) => {
  commit('CREATING_ROLE', true);
  try {
    let newRole = await persistRole(data);
    newRole.rolePermissions = {permissions: newRolePermissions};
    commit('CREATING_ROLE', false);

    let roles = state.roles;
    if(!roles)
      roles = [];

    roles.push(newRole);
    commit('SET_ROLES', roles);
    router.replace('/userAccess/roles/');
    showSuccessToast("Role created!");
  } catch (error) {
    handleNetworkError(error, "Error creating role:");
    commit('CREATING_ROLE', false);
  }
}

export const setRoleBeingEdited = ({ state, commit }, roleId) => {
  if(!roleId)
    commit('SET_ROLE_BEING_EDITED', null);

  if(!state.roles)
    return;

  const role = state.roles.find(role => role._id === roleId);
  commit('SET_ROLE_BEING_EDITED', role);
}

export const updateRole = async ({ commit, state }, {data, rolePermissions}) => {
  commit('CREATING_ROLE', true);
  try {
    const updatedRole = await updateRoleDetails(data);
    commit('CREATING_ROLE', false);

    let roles = state.roles;
    if(!roles)
      roles = [];

    roles = roles.map(role => {
      if(role._id === updatedRole._id){
        return {...updatedRole, rolePermissions};
      }
      return role;
    });

    commit('SET_ROLES', roles);
    router.replace('/userAccess/roles/');
    showSuccessToast("Role updated");
  } catch (error) {
    handleNetworkError(error, "Error creating role:");
    commit('CREATING_ROLE', false);
  }
}

export const approveRole = async ({ commit, state }, roleId) => {
  let roles = state.roles;
  if(!roles)
    roles = [];

  function setRoleApprovingStatus(approving, approvalStatus){
    roles = roles.map(role => {
      if(role._id === roleId){
        let updatedRole = {...role, approving};
        if(approvalStatus !== undefined)
          updatedRole.approvalStatus = approvalStatus;

        return updatedRole;
      }
      return role;
    });
    commit('SET_ROLES', roles);
  }

  try {
    setRoleApprovingStatus(true);
    await markRoleAsApproved(roleId);
    setRoleApprovingStatus(false, 1);

    showSuccessToast("Staff user approved");
  } catch (error) {
    handleNetworkError(error, "Error approving staff user:");
    setRoleApprovingStatus(false);
  }
}

export const editRolePermissions = async ({ commit, state }, {data, addedPermissions}) => {
  commit('CREATING_ROLE', true);
  try {
    await updateRolePermissions(data);
    commit('CREATING_ROLE', false);

    let roles = state.roles;
    roles = roles.map(role => {
      if(role._id === data.role && role.rolePermissions){
        const oldPermissions = role.rolePermissions.permissions;
        const removedPermissions = data.permissions.removed;
        let newPermissions = oldPermissions.filter(p => removedPermissions.indexOf(p._id) === -1);
        newPermissions = newPermissions.concat(addedPermissions);
        role.rolePermissions = {permissions: newPermissions};
      }
      return role;
    });

    commit('SET_ROLES', roles);
    router.replace('/userAccess/roles/');
    showSuccessToast("Role permissions updated");
  } catch (error) {
    handleNetworkError(error, "Error setting role permissions");
    commit('CREATING_ROLE', false);
  }
}

export const setRoleBeingDeleted = ({ state, commit }, roleId) => {
  if(!roleId)
    commit('SET_ROLE_BEING_DELETED', null);

  if(!state.roles)
    return;

  const role = state.roles.find(role => role._id === roleId);
  commit('SET_ROLE_BEING_DELETED', role);
}

export const removeRole = async ({ commit, state }, roleId) => {
  commit('DELETING_ROLE', true);
  try {
    await deleteRole(roleId);
    commit('DELETING_ROLE', false);
    router.replace('/userAccess/roles/');

    let roles = state.roles;
    if(!roles)
      roles = [];

    roles = roles.filter(role => role._id !== roleId);

    commit('SET_ROLES', roles);
    showSuccessToast("Role deleted");
  } catch (error) {
    handleNetworkError(error, "Error creating role:");
    commit('DELETING_ROLE', false);
  }
}

export const getStaffUsersForRole = async ({ commit, state }, roleId) => {
  commit('FETCHING_ROLE_STAFF_USERS', true);
  try {
    let staffUsers = await fetchStaffUsersForRole(roleId);
    commit('FETCHING_ROLE_STAFF_USERS', false);

    let roles = state.roles;
    if(!roles)
      roles = [];

    roles = roles.map(role => {
      if(role._id === roleId){
        staffUsers = staffUsers.map(user => {
          user.role = role._id;
          user.userRole = role;
          return user;
        });

        const roleWithStaff = {...role, staffUsers};
        commit('SET_ROLE_BEING_DELETED', roleWithStaff);
        return roleWithStaff;
      }

      return role;
    });

    commit('SET_ROLES', roles);
  } catch (error) {
    handleNetworkError(error, "Error fetching staff users for role:");
    commit('FETCHING_ROLE_STAFF_USERS', false);
  }
}

export const getPermissions = async ({ commit }) => {
  commit('FETCHING_PERMISSIONS', true);
  commit('SET_PERMISSIONS', []);
  try {
    const permissions = await fetchPermissions();
    commit('FETCHING_PERMISSIONS', false);
    commit('SET_PERMISSIONS', permissions);
  } catch (error) {
    handleNetworkError(error, "Error fetching savings data:");
    commit('FETCHING_PERMISSIONS', false);
    commit('SET_PERMISSIONS', null);
  }
}
